import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import BaseOverviewStore, {
    type ApiResponse,
    type BaseStoreState,
} from '../../../../shared/stores/baseOverviewStore';
import { formatName } from '../../../../utils/helper';

interface RequestStoreState extends BaseStoreState {
    filterStatus: string;
    filterRequestType: string;
    filterUser: string;
    filterManager: any;
}

/**
 * Load all requests and make it possible to manage them
 */
class RequestOverviewStore extends BaseOverviewStore<any> {
    protected storageKey(): string {
        return 'request';
    }

    // Implement required abstract methods
    protected defaultSortField(): string {
        return 'id';
    }

    protected getApiEndpoint(): string {
        return 'request/manager';
    }

    protected getApiPath(): string {
        return 'query';
    }

    // Request-specific properties
    public refreshTimer: any = null;
    public available_request_types: Array<{ id: number; name: string }> = [];
    public available_users: Array<{ id: number; name: string }> = [];
    public available_managers: Array<{ id: number; name: string }> = [];
    public filterStatus: string = '';
    public filterRequestType: string = '';
    public filterUser: string = '';
    public filterManager: any = null;
    public requestDeleteConfirmation: any = null;
    public requestApproveConfirmation: any = null;
    public requestRejectConfirmation: any = null;

    constructor(sdk: any) {
        super(sdk);

        // Override default sort order for orders
        this.sortOrder = -1;

        // Make request-specific properties observable
        makeObservable(this, {
            refreshTimer: observable,
            available_request_types: observable,
            available_users: observable,
            available_managers: observable,
            filterStatus: observable,
            filterRequestType: observable,
            filterUser: observable,
            filterManager: observable,
            requestDeleteConfirmation: observable,
            requestApproveConfirmation: observable,
            requestRejectConfirmation: observable,
            onDeleteRequest: action,
            onDeleteRequestCancel: action,
            onDeleteRequestConfirm: action,
            onApproveRequest: action,
            onApproveRequestCancel: action,
            onApproveRequestConfirm: action,
            onRejectRequest: action,
            onRejectRequestCancel: action,
            onRejectRequestConfirm: action,
            applyFilterStatus: action,
            applyFilterRequestType: action,
            applyFilterUser: action,
            applyFilterManager: action,
            clearAllFilters: action,
            fetchAvailableRequestTypes: action,
            fetchAvailableUsers: action,
            fetchAvailableManagers: action,
            refresh: action,
            resetRefreshTimer: action,
            allRequests: computed,
        });

        // Re-load from storage now that observables are set up
        this.loadFromLocalStorage();
    }

    // Order-specific methods
    public resetRefreshTimer(): void {
        if (this.refreshTimer) {
            clearTimeout(this.refreshTimer);
            this.refreshTimer = null;
        }
    }

    public refresh(): void {
        if (this.refreshTimer) {
            return;
        }
        const that = this;
        this.refreshTimer = setTimeout(() => that.resetRefreshTimer(), 5000);
        this.fetchList();
    }

    protected buildApiParams(page: number): any {
        return {
            sortvalue: this.sortField,
            sort: this.sortOrder === 1 ? 'asc' : 'desc',
            search: this.searchValue || '',
            page,
            ...(this.filterManager?.id
                ? {
                      manager_id: this.filterManager?.id,
                  }
                : {}),
            ...(this.filterStatus
                ? {
                      status: this.filterStatus,
                  }
                : {}),
        };
    }

    protected processApiResponse(response: ApiResponse): any[] {
        return response.json.data.map((item: any) => {
            return {
                ...item,
                employee_name:
                    item?.employee === null
                        ? '-'
                        : formatName(item.employee.firstname, item.employee.lastname),
            };
        });
    }

    protected getStorageState(): RequestStoreState {
        return {
            sortOrder: this.sortOrder,
            sortField: this.sortField,
            searchValue: this.searchValue,
            filterStatus: this.filterStatus,
            filterRequestType: this.filterRequestType,
            filterUser: this.filterUser,
            filterManager: this.filterManager,
        };
    }

    protected applyStorageState(state: RequestStoreState): void {
        runInAction(() => {
            this.sortOrder = state.sortOrder || -1; // Default to desc for requests
            this.sortField = state.sortField || this.defaultSortField();
            this.searchValue = state.searchValue || '';
            this.filterStatus = state.filterStatus || '';
            this.filterRequestType = state.filterRequestType || '';
            this.filterUser = state.filterUser || '';
            this.filterManager = state.filterManager || null;
        });
    }

    public applyFilterStatus(status: string): void {
        this.filterStatus = status;
        this.saveToLocalStorage();
        this.fetchList();
    }

    public applyFilterRequestType(requestTypeId: string): void {
        this.filterRequestType = requestTypeId;
        this.saveToLocalStorage();
        this.fetchList();
    }

    public applyFilterUser(userId: string): void {
        this.filterUser = userId;
        this.saveToLocalStorage();
        this.fetchList();
    }

    public applyFilterManager(manager: any | null): void {
        this.filterManager = manager;
        this.saveToLocalStorage();
        this.fetchList();
    }

    public clearAllFilters(): void {
        this.filterStatus = '';
        this.filterRequestType = '';
        this.filterUser = '';
        this.filterManager = null;
        this.saveToLocalStorage();
        this.fetchList();
    }

    public onDeleteRequest(request: any): void {
        this.requestDeleteConfirmation = request;
    }

    public onDeleteRequestCancel(): void {
        this.requestDeleteConfirmation = null;
    }

    public async onDeleteRequestConfirm(reason: string): Promise<void> {
        if (this.requestDeleteConfirmation?.id && reason) {
            const result = await this.sdk.fetchApi('request/manager', 'delete', {
                request_id: this.requestDeleteConfirmation.id,
                reason: reason,
            });
            if (result?.status === 200) {
                this.fetchList();
            }
        }
        this.requestDeleteConfirmation = null;
    }

    public onApproveRequest(request: any): void {
        this.requestApproveConfirmation = request;
    }

    public onApproveRequestCancel(): void {
        this.requestApproveConfirmation = null;
    }

    public async onApproveRequestConfirm(comment: string = ''): Promise<void> {
        if (this.requestApproveConfirmation?.id) {
            const result = await this.sdk.fetchApi('request/manager', 'approve', {
                request_id: this.requestApproveConfirmation.id,
                approver_comment: comment,
            });
            if (result?.status === 200) {
                this.fetchList();
            }
        }
        this.requestApproveConfirmation = null;
    }

    public onRejectRequest(request: any): void {
        this.requestRejectConfirmation = request;
    }

    public onRejectRequestCancel(): void {
        this.requestRejectConfirmation = null;
    }

    public async onRejectRequestConfirm(reason: string): Promise<void> {
        if (this.requestRejectConfirmation?.id && reason) {
            const result = await this.sdk.fetchApi('request/manager', 'reject', {
                request_id: this.requestRejectConfirmation.id,
                approver_comment: reason,
            });
            if (result?.status === 200) {
                this.fetchList();
            }
        }
        this.requestRejectConfirmation = null;
    }

    public async fetchAvailableRequestTypes(force: boolean = false): Promise<void> {
        if (!force && this.available_request_types.length > 0) {
            return;
        }
        // For now, we'll use hardcoded request types. Later this could be fetched from API
        runInAction(() => {
            this.available_request_types = [
                { id: 1, name: 'Urlaub' },
                { id: 2, name: 'Krankheit' },
                { id: 3, name: 'Sonderurlaub' },
            ];
        });
    }

    public async fetchAvailableUsers(force: boolean = false): Promise<void> {
        if (!force && this.available_users.length > 0) {
            return;
        }
        // For now, we'll skip this. Later this could be fetched from an employee endpoint
        runInAction(() => {
            this.available_users = [];
        });
    }

    public async fetchAvailableManagers(force: boolean = false): Promise<void> {
        if (!force && this.available_managers.length > 0) {
            return;
        }
        const managerResponse: any = await this.sdk.fetchApi('manager', 'query', {});

        runInAction(() => {
            if (managerResponse?.status === 200) {
                this.available_managers = managerResponse.json.data.map((manager: any) => ({
                    id: manager.id,
                    name: `${manager.firstname} ${manager.lastname}`,
                }));
            }
        });
    }

    public async onDialogLeave(dialog: string, event: any = null): Promise<void> {
        if (
            dialog === 'request' &&
            (event?.event === 'SAVED' || event?.event === 'ACCEPTED' || event?.event === 'REJECTED')
        ) {
            await this.fetchList(this.latestPage);
        } else {
            await super.onDialogLeave(dialog, event);
        }
    }

    public onLeaveStore(): void {
        super.onLeaveStore();

        if (this.refreshTimer) {
            clearTimeout(this.refreshTimer);
        }
    }

    // Computed properties and aliases for compatibility
    get allRequests(): Array<any> | null {
        if (!this.list) return null;
        return this.list;
    }

    get requestList(): Array<any> {
        return this.list;
    }

    get requestListLoading(): boolean {
        return this.listLoading;
    }

    public fetchRequestList(page: number = 1): Promise<void> {
        return this.fetchList(page);
    }
}

export default RequestOverviewStore;
